import {
  lastAssistantMessageIsCompleteWithApprovalResponses,
  lastAssistantMessageIsCompleteWithToolCalls,
  type UIMessage,
} from 'ai';

type AvaAIAvailability = {
  endpoint: string;
  enableAvaAI: boolean;
  isMobileLayout: boolean;
};

export const AvaAIErrorCode = {
  SessionLimitExceeded: 'CHAT_SESSION_LIMIT_EXCEEDED',
} as const;
export type AvaAIErrorCode = (typeof AvaAIErrorCode)[keyof typeof AvaAIErrorCode];
export const AVA_AI_SESSION_LIMIT_ERROR_MESSAGE =
  'This chat session has reached its limit. Start a new session to continue.';
const GENERIC_AVA_AI_ERROR_MESSAGE = 'Something went wrong.';

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function getAvaAIErrorCode(error: Error | undefined): AvaAIErrorCode | undefined {
  if (error === undefined) {
    return undefined;
  }

  let parsedError: unknown;
  try {
    parsedError = JSON.parse(error.message) as unknown;
  } catch {
    return undefined;
  }

  if (!isRecord(parsedError) || !isRecord(parsedError['Error'])) {
    return undefined;
  }

  const code = parsedError['Error']['Code'];
  return code === AvaAIErrorCode.SessionLimitExceeded ? code : undefined;
}

export function getAvaAIErrorMessage(error: Error | undefined): string {
  return getAvaAIErrorCode(error) === AvaAIErrorCode.SessionLimitExceeded
    ? AVA_AI_SESSION_LIMIT_ERROR_MESSAGE
    : GENERIC_AVA_AI_ERROR_MESSAGE;
}

/** Trims the configured endpoint and drops trailing slashes so an API path can be appended directly. */
export function normalizeAvaAIEndpoint(endpoint?: string): string {
  return endpoint?.trim().replace(/\/+$/, '') ?? '';
}

export function isAvaAIEnabled({ endpoint, enableAvaAI, isMobileLayout }: AvaAIAvailability): boolean {
  return Boolean(endpoint) && enableAvaAI && !isMobileLayout;
}

export function lastAvaAIAssistantStepIsComplete(messages: UIMessage[]): boolean {
  return (
    lastAssistantMessageIsCompleteWithToolCalls({ messages }) ||
    lastAssistantMessageIsCompleteWithApprovalResponses({ messages })
  );
}

/**
 * Drops an incomplete final assistant step so the completed tool or approval output before it can
 * be retried in place. Returns undefined when nothing retryable remains.
 */
export function trimIncompleteAvaAIAssistantStep<Message extends UIMessage>(
  messages: Message[]
): Message[] | undefined {
  const lastMessage = messages[messages.length - 1];
  if (lastMessage?.role !== 'assistant') {
    return undefined;
  }

  const lastStepStartIndex = lastMessage.parts.findLastIndex(part => part.type === 'step-start');
  if (lastStepStartIndex <= 0) {
    return undefined;
  }

  const trimmedMessages = [
    ...messages.slice(0, -1),
    { ...lastMessage, parts: lastMessage.parts.slice(0, lastStepStartIndex) },
  ];

  return lastAvaAIAssistantStepIsComplete(trimmedMessages) ? trimmedMessages : undefined;
}
